/** Browser-safe saved chart contract. Recipes and numerical results stay server-side. */
import { z } from 'zod';
import type { StudioSpec } from './studioModel.js';

export const SAVED_CHART_SCHEMA = 'marsmen.saved-chart.v1' as const;
export const SAVED_CHART_LIMITS = {
  maxGrants: 50,
  maxGrantDays: 30,
  maxList: 200,
} as const;
const column = z.string().trim().min(1).max(255);
export const savedChartStudioSpecSchema = z
  .object({
    type: z.enum([
      'line',
      'area',
      'bar',
      'stacked',
      'scatter',
      'heatmap',
      'table',
    ]),
    x: column.nullable(),
    ys: z.array(column).max(8),
    y2: z.array(column).max(8),
    group: column.nullable(),
    agg: z.enum(['sum', 'avg', 'min', 'max', 'count']),
    sort: z.enum(['x-asc', 'x-desc', 'value-desc', 'value-asc']),
    topN: z.number().int().min(1).max(500).nullable(),
    log: z.boolean(),
    zero: z.boolean(),
    movingAvg: z.number().int().min(2).max(365).nullable(),
    target: z.number().finite().nullable(),
    format: z.enum(['auto', 'number', 'currency', 'percent', 'compact']),
  })
  .strict();
export type SavedChartStudioSpec = StudioSpec;
export const savedChartDefinitionSchema = z
  .object({
    studio: savedChartStudioSpecSchema,
    hiddenSeries: z.array(z.string().max(500)).max(64),
    brush: z
      .object({ start: z.number().int().min(0), end: z.number().int().min(0) })
      .strict()
      .refine((value) => value.start <= value.end)
      .nullable(),
  })
  .strict();
export type SavedChartDefinition = {
  studio: SavedChartStudioSpec;
  hiddenSeries: string[];
  brush: { start: number; end: number } | null;
};
export const parseSavedChartDefinition = (
  value: unknown,
): SavedChartDefinition =>
  savedChartDefinitionSchema.parse(value) as SavedChartDefinition;
export const savedChartCoverageSchema = z
  .object({
    loadedRowCount: z.number().int().min(0).max(100000),
    totalRowCount: z.number().int().min(0).nullable(),
    truncated: z.boolean().nullable(),
  })
  .strict();
export type SavedChartCoverage = z.infer<typeof savedChartCoverageSchema>;
export type SavedChartSource = {
  queryExecutionId: string;
  receiptId: string;
  capturedAt: string;
  resultDigest: string | null;
  database: string;
  tables: string[];
  workgroup: string;
  columns: string[];
  /** Server observed page coverage. A missing total remains unknown. */
  coverage: SavedChartCoverage;
  /** Browser-reported loaded coverage, never authority for numerical evidence. */
  authoredCoverage: SavedChartCoverage | null;
  presentationDigest: string | null;
};
export type SavedChart = {
  schema: typeof SAVED_CHART_SCHEMA;
  id: string;
  revision: string;
  title: string;
  owner: string;
  createdAt: string;
  updatedAt: string;
  definition: SavedChartDefinition;
  source: SavedChartSource;
  permissions: {
    isOwner: boolean;
    canEdit: boolean;
    canShare: boolean;
    canExport: boolean;
  };
  grantExpiresAt: string | null;
};
export type SavedChartGrant = {
  id: string;
  chartId: string;
  revision: string;
  recipientEmail: string;
  createdAt: string;
  expiresAt: string;
  revokedAt: string | null;
};
export const savedChartIdSchema = z.string().uuid();
export const savedChartCreateSchema = z
  .object({
    title: z.string().trim().min(1).max(160),
    queryExecutionId: savedChartIdSchema,
    definition: savedChartDefinitionSchema,
    idempotencyKey: z
      .string()
      .regex(/^[a-zA-Z0-9_-]{16,128}$/)
      .optional(),
    authoredCoverage: savedChartCoverageSchema.optional(),
  })
  .strict();
export type SavedChartCreateInput = {
  title: string;
  queryExecutionId: string;
  definition: SavedChartDefinition;
  idempotencyKey?: string;
  authoredCoverage?: SavedChartCoverage;
};
export const savedChartUpdateSchema = z
  .object({
    expectedRevision: savedChartIdSchema,
    title: z.string().trim().min(1).max(160).optional(),
    definition: savedChartDefinitionSchema.optional(),
  })
  .strict()
  .refine(
    (value) => value.title !== undefined || value.definition !== undefined,
  );
export const savedChartGrantSchema = z
  .object({
    revision: savedChartIdSchema,
    recipientEmail: z.email().max(254),
    expiresAt: z.iso.datetime(),
  })
  .strict();
export const savedChartDefinitionFromPresentation = (presentation: {
  spec: {
    type: string;
    x?: string | null;
    y?: string | null;
    group?: string | null;
  };
  studio?: SavedChartDefinition;
}): SavedChartDefinition =>
  presentation.studio
    ? parseSavedChartDefinition(presentation.studio)
    : {
        studio: {
          type:
            presentation.spec.type === 'stacked'
              ? 'stacked'
              : presentation.spec.type === 'bar'
                ? 'bar'
                : presentation.spec.type === 'table'
                  ? 'table'
                  : 'line',
          x: presentation.spec.x ?? null,
          ys: presentation.spec.y ? [presentation.spec.y] : [],
          y2: [],
          group: presentation.spec.group ?? null,
          agg: 'sum',
          sort: 'x-asc',
          topN: null,
          log: false,
          zero: false,
          movingAvg: null,
          target: null,
          format: 'auto',
        },
        hiddenSeries: [],
        brush: null,
      };
